import ManagedTable, { ManagedColumn, ManagedTableProps } from 'app/common/table/ManagedTable';
import { MonitoredRegulationOutlook, RegulationOutlook } from 'app/model/SOL49';
import useWindowDimensions from 'app/common/hooks/useWindowDimensions';
import React, { CSSProperties } from 'react';
import { RegulationReasonType } from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import { RegulationOutlookMoreMenu } from 'app/components/dashboard/network/flows/RegulationOutlookLoad';
import { CheckCircleFilled } from '@ant-design/icons';

export class RegulationOutlookColumn extends ManagedColumn<MonitoredRegulationOutlook> {}

export const REGULATION_OUTLOOK_SELECTED_COLUMN = new RegulationOutlookColumn(
    '',
    '2em',
    () => (
        <div className={'check-circle'}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

export const REGULATION_OUTLOOK_ACTIONS_COLUMN = new RegulationOutlookColumn(
    '',
    '2em',
    ({ regulationOutlook }) => (
        <Flex className="more-button" onClick={e => e.stopPropagation()}>
            <RegulationOutlookMoreMenu regulationOutlook={regulationOutlook} />
        </Flex>
    ),
    { name: 'actions', className: () => 'monitored-measure-actions' },
);

const getReasonPrediction = (regOutlook: RegulationOutlook, type: RegulationReasonType) => {
    return regOutlook.reasons.find(reason => reason.reason === type).prediction;
};

const renderPrediction = (prediction: number) => `${(prediction * 100).toFixed(0)}%`;

const DEFAULT_STYLE: CSSProperties = { textAlign: 'center' };

const getCellStyle = (value: RegulationOutlook, type: RegulationReasonType) => {
    let style: CSSProperties = DEFAULT_STYLE;
    const worstReason = value.reasons?.reduce((prev, curr) => (prev.prediction > curr.prediction ? prev : curr));
    if (worstReason.reason === type) {
        style = { ...style, fontWeight: 'bold', color: 'white' };
    }
    return style;
};

export class StandardRegulationOutlookColumn {
    public static readonly AERODROME = new RegulationOutlookColumn(
        'AERODROME',
        '7em',
        ({ regulationOutlook }) => regulationOutlook.aerodromeId,
        {
            cellStyle: () => DEFAULT_STYLE,
        },
    );
    public static readonly PREDICTION = new RegulationOutlookColumn(
        'PREDICTION',
        '7em',
        ({ regulationOutlook }) => regulationOutlook.prediction,
        {
            cellStyle: () => DEFAULT_STYLE,
            formatter: renderPrediction,
        },
    );
    public static readonly WEATHER_REASON = new RegulationOutlookColumn(
        'WEATHER',
        '6em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.WEATHER),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.WEATHER),
            formatter: renderPrediction,
        },
    );
    public static readonly ATC_CAPACITY_REASON = new RegulationOutlookColumn(
        'ATC CAPACITY',
        '8em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.ATC_CAPACITY),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.ATC_CAPACITY),
            formatter: renderPrediction,
        },
    );
    public static readonly AD_CAPACITY_REASON = new RegulationOutlookColumn(
        'AD CAPACITY',
        '8em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.AERODROME_CAPACITY),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.AERODROME_CAPACITY),
            formatter: renderPrediction,
        },
    );
    public static readonly STRIKE_REASON = new RegulationOutlookColumn(
        'ATC_IND',
        '6em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.ATC_INDUSTRIAL_ACTION),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.ATC_INDUSTRIAL_ACTION),
            formatter: renderPrediction,
        },
    );
    public static readonly EVENTS_REASON = new RegulationOutlookColumn(
        'EVENT',
        '6em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.SPECIAL_EVENT),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.SPECIAL_EVENT),
            formatter: renderPrediction,
        },
    );
    public static readonly OTHER_REASON = new RegulationOutlookColumn(
        'OTHER',
        '6em',
        ({ regulationOutlook }) => getReasonPrediction(regulationOutlook, RegulationReasonType.OTHERS),
        {
            cellStyle: (_, { regulationOutlook }: MonitoredRegulationOutlook) =>
                getCellStyle(regulationOutlook, RegulationReasonType.OTHERS),
            formatter: renderPrediction,
        },
    );
}

class RegulationOutlookTableBase extends ManagedTable<MonitoredRegulationOutlook> {}

const RegulationOutlookTable = (props: ManagedTableProps<MonitoredRegulationOutlook>) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.45 * width) / 100;

    return <RegulationOutlookTableBase fontSize={fontSize} {...props} />;
};

export default RegulationOutlookTable;
